//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import {
  IAnalyticsProviderSettings,
  IAnalyticsProvidersSettings,
} from '@account-settings/redux/analyticsProviders/analyticsProvidersModel';
import * as analyticsSettingsApi from '@apis/analytics-settings/analyticsSettingsApi';
import { IGetGA4ResponseMetadata } from '@apis/analytics-settings/analyticsSettingsApiModel';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import GaProfileId from '@models/GaProfileId';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';
import { recordEvent } from '@tracker/tracker';
import {
  TRACKING_ACTION,
  TRACKING_CATEGORY,
  TRACKING_LABEL,
} from '@tracker/trackerModel';
import { RequestError } from '@utils/sessionFetch';

const initialState: IAnalyticsProvidersSettings = {
  providerSettings: {
    adobeAnalyticsSettings: undefined,
    adobeAnalyticsOAuthSettings: undefined,
    googleAnalytics4Settings: undefined,
    googleAnalytics4BigQueryDataset: undefined,
    pixelSettings: undefined,
  },
  oauthEndpoint: undefined,
  modalStates: {
    isChangeGoogleAnalyticsProfileModalOpen: false,
    isGoogleAnalyticsFirstTimeSetupModalOpen: false,
    isConnectAdobeOAuthModalOpen: false,
  },
  errors: {
    fetchSettingsError: undefined,
    connectGAError: undefined,
    disconnectGAError: undefined,
    disconnectAdobeError: undefined,
    connectAdobeOAuthError: undefined,
    disconnectAdobeOAuthError: undefined,
    updateGAProfileError: undefined,
  },
  flags: {
    isFetchingSettings: false,
    isConnectingGA: false,
    isDisconnectingGA: false,
    isDisconnectingAdobe: false,
    isConnectingAdobeOAuth: false,
    isDisconnectingAdobeOAuth: false,
    isUpdatingGAProfile: false,
  },
};

const { actions, reducer } = createSlice({
  name: SliceNames.AccountSettingsAnalyticsProviders,
  initialState,
  reducers: {
    requestAnalyticsProvidersSettings(state) {
      state.flags.isFetchingSettings = true;
      state.errors.fetchSettingsError = undefined;
    },
    receiveAnalyticsProvidersSettingsSucceeded(
      state,
      action: PayloadAction<IAnalyticsProviderSettings>,
    ) {
      state.flags.isFetchingSettings = false;
      state.providerSettings = {
        ...action.payload,
      };
    },
    receiveAnalyticsProvidersSettingsFailed(
      state,
      action: PayloadAction<string>,
    ) {
      state.flags.isFetchingSettings = false;
      state.errors.fetchSettingsError = action.payload;
    },
    requestConnectGA(state) {
      state.flags.isConnectingGA = true;
      state.errors.connectGAError = undefined;
    },
    receiveConnectGASucceeded(state, action: PayloadAction<string>) {
      state.oauthEndpoint = action.payload;
      state.flags.isConnectingGA = false;
    },
    receiveConnectGAFailed(state, action: PayloadAction<string>) {
      state.flags.isConnectingGA = false;
      state.errors.connectGAError = action.payload;
    },
    requestDisconnectGA(state) {
      state.flags.isDisconnectingGA = true;
      state.errors.disconnectGAError = undefined;
    },
    receiveDisconnectGASucceeded(state) {
      state.flags.isDisconnectingGA = false;
    },
    receiveDisconnectGAFailed(state, action: PayloadAction<string>) {
      state.flags.isDisconnectingGA = false;
      state.errors.disconnectGAError = action.payload;
    },
    requestUpdateGAProfile(state) {
      state.flags.isUpdatingGAProfile = true;
      state.errors.updateGAProfileError = undefined;
    },
    receiveUpdateGAProfileSucceeded(state) {
      state.flags.isUpdatingGAProfile = false;
    },
    receiveUpdateGAProfileFailed(state, action: PayloadAction<string>) {
      state.flags.isUpdatingGAProfile = false;
      state.errors.updateGAProfileError = action.payload;
    },
    requestDisconnectAdobe(state) {
      state.flags.isDisconnectingAdobe = true;
      state.errors.disconnectAdobeError = undefined;
    },
    receiveDisconnectAdobeSucceeded(state) {
      state.flags.isDisconnectingAdobe = false;
    },
    receiveDisconnectAdobeFailed(state, action: PayloadAction<string>) {
      state.flags.isDisconnectingAdobe = false;
      state.errors.disconnectAdobeError = action.payload;
    },
    setIsChangeGoogleAnalyticsProfileModalOpen(
      state,
      action: PayloadAction<boolean>,
    ) {
      state.modalStates.isChangeGoogleAnalyticsProfileModalOpen =
        action.payload;
    },
    setIsGoogleAnalyticsFirstTimeSetupModalOpen(
      state,
      action: PayloadAction<boolean>,
    ) {
      state.modalStates.isGoogleAnalyticsFirstTimeSetupModalOpen =
        action.payload;
    },
    setIsConnectAdobeOAuthModalOpen(state, action: PayloadAction<boolean>) {
      state.modalStates.isConnectAdobeOAuthModalOpen = action.payload;
    },
    requestConnectAdobeOAuth(state) {
      state.flags.isConnectingAdobeOAuth = true;
      state.errors.connectAdobeOAuthError = undefined;
    },
    receiveConnectAdobeOAuthSucceeded(state) {
      state.flags.isConnectingAdobeOAuth = false;
    },
    receiveConnectAdobeOAuthFailed(state, action: PayloadAction<string>) {
      state.flags.isConnectingAdobeOAuth = false;
      state.errors.connectAdobeOAuthError = action.payload;
    },
    requestDisconnectAdobeOAuth(state) {
      state.flags.isDisconnectingAdobeOAuth = true;
      state.errors.disconnectAdobeOAuthError = undefined;
    },
    receiveDisconnectAdobeOAuthSucceeded(state) {
      state.flags.isDisconnectingAdobeOAuth = false;
    },
    receiveDisconnectAdobeOAuthFailed(state, action: PayloadAction<string>) {
      state.flags.isDisconnectingAdobeOAuth = false;
      state.errors.disconnectAdobeOAuthError = action.payload;
    },
  },
});

export const getAnalyticsProviderSettings = createThunk(
  () => async (dispatch) => {
    dispatch(actions.requestAnalyticsProvidersSettings());
    try {
      const [
        adobeAnalyticsSettings,
        adobeAnalyticsOAuthSettings,
        googleAnalytics4Settings,
        googleAnalytics4BigQueryDataset,
        pixelSettings,
      ] = await Promise.all([
        analyticsSettingsApi.getAdobeWebServiceSettings(),
        analyticsSettingsApi.getAdobeOAuthSettings(),
        analyticsSettingsApi.getGA4Settings(),
        analyticsSettingsApi.getGA4BigQueryDataset(),
        analyticsSettingsApi.getCuralatePixelSettings(),
      ]);

      let googleAnalytics4ResponseMetadata:
        | IGetGA4ResponseMetadata[]
        | undefined;
      if (googleAnalytics4Settings.currentPropertyId) {
        googleAnalytics4ResponseMetadata =
          await analyticsSettingsApi.getGA4ResponseMetadata(
            GaProfileId(googleAnalytics4Settings.currentPropertyId),
          );
      }

      dispatch(
        actions.receiveAnalyticsProvidersSettingsSucceeded({
          adobeAnalyticsSettings,
          adobeAnalyticsOAuthSettings,
          googleAnalytics4Settings,
          googleAnalytics4BigQueryDataset,
          googleAnalytics4ResponseMetadata,
          pixelSettings,
        }),
      );
    } catch (_e) {
      dispatch(
        actions.receiveAnalyticsProvidersSettingsFailed(
          'An error occurred when trying to get your analytics provider settings.',
        ),
      );
    }
  },
);

export const connectGA = createThunk(() => async (dispatch) => {
  dispatch(actions.requestConnectGA());
  try {
    const response = await analyticsSettingsApi.getGAOAuthURL();
    if (response.url) {
      recordEvent(
        TRACKING_CATEGORY.SETTINGS,
        TRACKING_ACTION.CONNECT_ACCOUNT,
        TRACKING_LABEL.SUCCESS,
      );

      dispatch(actions.receiveConnectGASucceeded(response.url));
    } else {
      throw new Error(
        'An error occurred when trying to connect your Google Analytics account.',
      );
    }
  } catch (e) {
    const error =
      'An error occurred when trying to connect your Google Analytics account.';

    recordEvent(
      TRACKING_CATEGORY.SETTINGS,
      TRACKING_ACTION.CONNECT_ACCOUNT,
      TRACKING_LABEL.FAIL,
    );
    dispatch(
      toastNotificationsActions.addToastNotification({
        title: error,
        type: ToastType.ERROR,
      }),
    );
    dispatch(actions.receiveConnectGAFailed(error));
  }
});

export const disconnectGA = createThunk(() => async (dispatch) => {
  dispatch(actions.requestDisconnectGA());
  try {
    await analyticsSettingsApi.deauthGAAccount();
    recordEvent(
      TRACKING_CATEGORY.SETTINGS,
      TRACKING_ACTION.DISCONNECT_ACCOUNT,
      TRACKING_LABEL.SUCCESS,
    );
    dispatch(
      toastNotificationsActions.addToastNotification({
        title: 'Google Analytics account successfully disconnected!',
        type: ToastType.SUCCESS,
      }),
    );
    dispatch(actions.receiveDisconnectGASucceeded());
    dispatch(getAnalyticsProviderSettings());
  } catch (e) {
    const error =
      'An error occurred when trying to disconnect your Google Analytics account.';
    recordEvent(
      TRACKING_CATEGORY.SETTINGS,
      TRACKING_ACTION.DISCONNECT_ACCOUNT,
      TRACKING_LABEL.FAIL,
    );
    dispatch(
      toastNotificationsActions.addToastNotification({
        title: error,
        type: ToastType.ERROR,
      }),
    );
    dispatch(actions.receiveDisconnectGAFailed(error));
  }
});

export const updateGAProfile = createThunk(
  (profileId: GaProfileId) => async (dispatch) => {
    dispatch(actions.requestUpdateGAProfile());
    try {
      // Encode profileId in case of GA4 property
      // e.g properties/123456789 needs to be encoded
      const encodedProfileId = GaProfileId(
        encodeURIComponent(profileId as string),
      );
      await analyticsSettingsApi.updateGAProfile(encodedProfileId);

      recordEvent(
        TRACKING_CATEGORY.SETTINGS,
        TRACKING_ACTION.GA_SWITCH_PROFILE,
        TRACKING_LABEL.SUCCESS,
      );
      dispatch(actions.receiveUpdateGAProfileSucceeded());
      dispatch(getAnalyticsProviderSettings());
      dispatch(setIsChangeGoogleAnalyticsProfileModalOpen(false));
      dispatch(setIsGoogleAnalyticsFirstTimeSetupModalOpen(false));
    } catch (e) {
      const error =
        'An error occurred when trying to update your Google Analytics profile.';
      recordEvent(
        TRACKING_CATEGORY.SETTINGS,
        TRACKING_ACTION.GA_SWITCH_PROFILE,
        TRACKING_LABEL.FAIL,
      );
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: error,
          type: ToastType.ERROR,
        }),
      );
      dispatch(actions.receiveConnectGAFailed(error));
    }
  },
);

export const deleteBigQueryDataset = createThunk(() => async (dispatch) => {
  try {
    await analyticsSettingsApi.deleteGA4BigQueryDataset();
    dispatch(getAnalyticsProviderSettings());
  } catch (e) {
    // DELETE on non-existing resource is ok, no need to alert of any error
    if (e instanceof RequestError) {
      if (e.statusCode === 404) {
        return;
      }
    }

    dispatch(
      toastNotificationsActions.addToastNotification({
        title:
          'An error occurred when trying to disconnect your Google Analytics BigQuery dataset.',
        type: ToastType.ERROR,
      }),
    );
  }
});

export const subscribeAnalyticsHubListing = createThunk(
  (url: string) => async (dispatch) => {
    try {
      await analyticsSettingsApi.subscribeAnalyticsHubListing(url);
      dispatch(getAnalyticsProviderSettings());
    } catch (e) {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title:
            'An error occurred when trying to subscribe to your Google Analytics Hub listing.',
          type: ToastType.ERROR,
        }),
      );
    }
  },
);

export const disconnectAdobeWebService = createThunk(() => async (dispatch) => {
  dispatch(actions.requestDisconnectAdobe());
  try {
    await analyticsSettingsApi.disconnectAdobeAnalyticsWebservice();

    recordEvent(
      TRACKING_CATEGORY.SETTINGS,
      TRACKING_ACTION.DISCONNECT_ACCOUNT,
      TRACKING_LABEL.SUCCESS,
    );

    dispatch(
      toastNotificationsActions.addToastNotification({
        title: 'Adobe Analytics account successfully disconnected!',
        type: ToastType.SUCCESS,
      }),
    );
    dispatch(actions.receiveDisconnectAdobeSucceeded());
    dispatch(getAnalyticsProviderSettings());
  } catch (_e) {
    const error =
      'An error occurred when trying to disconnect your Adobe Analytics account.';
    recordEvent(
      TRACKING_CATEGORY.SETTINGS,
      TRACKING_ACTION.DISCONNECT_ACCOUNT,
      TRACKING_LABEL.FAIL,
    );
    dispatch(
      toastNotificationsActions.addToastNotification({
        title: error,
        type: ToastType.ERROR,
      }),
    );
    dispatch(actions.receiveDisconnectAdobeFailed(error));
  }
});

export const connectAdobeOAuth = createThunk(
  (
    reportSuiteId: string,
    adobeClientId: string,
    sharedSecret: string,
    scopes: string,
    adobeCompanyId?: string,
  ) =>
    async (dispatch) => {
      dispatch(actions.requestConnectAdobeOAuth());

      try {
        await analyticsSettingsApi.connectAdobeAnalyticsOAuth({
          reportSuiteId,
          adobeClientId,
          sharedSecret,
          scopes,
          // TODO: PD-319318: Remove adobeCompanyId fully once AnalyticsConfigurationMigration flag is removed
          ...(adobeCompanyId && { adobeCompanyId }),
        });

        recordEvent(
          TRACKING_CATEGORY.SETTINGS,
          TRACKING_ACTION.CONNECT_ACCOUNT,
          TRACKING_LABEL.SUCCESS,
        );

        dispatch(actions.receiveConnectAdobeOAuthSucceeded());
        void dispatch(getAnalyticsProviderSettings());
        dispatch(setIsConnectAdobeOAuthModalOpen(false));
      } catch (_e) {
        const error =
          'An error occurred when trying to connect your Adobe Analytics account.';
        recordEvent(
          TRACKING_CATEGORY.SETTINGS,
          TRACKING_ACTION.CONNECT_ACCOUNT,
          TRACKING_LABEL.FAIL,
        );

        dispatch(actions.receiveConnectAdobeOAuthFailed(error));
      }
    },
);

export const disconnectAdobeOAuth = createThunk(() => async (dispatch) => {
  dispatch(actions.requestDisconnectAdobeOAuth());
  try {
    await analyticsSettingsApi.disconnectAdobeAnalyticsOAuth();

    recordEvent(
      TRACKING_CATEGORY.SETTINGS,
      TRACKING_ACTION.DISCONNECT_ACCOUNT,
      TRACKING_LABEL.SUCCESS,
    );

    dispatch(
      toastNotificationsActions.addToastNotification({
        title: 'Adobe Analytics account successfully disconnected!',
        type: ToastType.SUCCESS,
      }),
    );
    dispatch(actions.receiveDisconnectAdobeOAuthSucceeded());
    dispatch(getAnalyticsProviderSettings());
  } catch (_e) {
    const error =
      'An error occurred when trying to disconnect your Adobe Analytics account.';
    recordEvent(
      TRACKING_CATEGORY.SETTINGS,
      TRACKING_ACTION.DISCONNECT_ACCOUNT,
      TRACKING_LABEL.FAIL,
    );

    dispatch(
      toastNotificationsActions.addToastNotification({
        title: error,
        type: ToastType.ERROR,
      }),
    );
    dispatch(actions.receiveDisconnectAdobeOAuthFailed(error));
  }
});

export const {
  setIsChangeGoogleAnalyticsProfileModalOpen,
  setIsConnectAdobeOAuthModalOpen,
  setIsGoogleAnalyticsFirstTimeSetupModalOpen,
} = actions;

export default reducer;
